const getEnv = (name: string): string => {
  const value = import.meta.env[name] as string;
  if (!value) {
    throw new Error(`Missing required env var: ${name}`);
  }
  return value;
};

export const getOptionalEnv = (name: string): string | undefined => {
  const value = import.meta.env[name] as string | undefined;
  return value || undefined;
};

/**
 * Returns the API base URL. An empty string means same-origin (relative URLs),
 * which is how CloudFront-hosted builds talk to the ALB via the distribution's
 * API behaviors. Local dev sets this to `http://localhost:${API_PORT}` via
 * `vite.config.ts`.
 *
 * Use this helper instead of `getEnv('VITE_API_SERVER_URL')` — the latter
 * throws when the value is an empty string, which breaks same-origin builds.
 */
export const getApiBaseUrl = (): string => {
  const value = import.meta.env.VITE_API_SERVER_URL as string | undefined;
  return value ?? '';
};

const GRAPHQL_WS_PATH = '/ws/graphql';

/**
 * Absolute `ws`/`wss` URL for a server WebSocket path, derived from
 * {@link getApiBaseUrl} with the scheme swapped. A same-origin build (empty
 * base) falls back to `window.location`, since WebSocket URLs cannot be
 * relative. Both sockets go through here so their scheme and origin handling
 * cannot drift apart.
 */
export const getWebSocketUrl = (path: string): string => {
  const base = getApiBaseUrl();
  if (base) {
    return `${base.replace(/^http/, 'ws')}${path}`;
  }
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${window.location.host}${path}`;
};

/** Absolute URL of the GraphQL subscriptions WebSocket. */
export const getGraphqlWsUrl = (): string => getWebSocketUrl(GRAPHQL_WS_PATH);

export default getEnv;

/**
 * The Stripe publishable key the Payment Element mounts with. Publishable by
 * definition — it can only tokenize, never charge — but it pairs with the
 * account, so test and live must not cross.
 */
export const getStripePublishableKey = (): string => getEnv('VITE_STRIPE_PUBLISHABLE_KEY');
